import React from 'react';
import { styled } from '@material-ui/core/styles';

import CallEnded from './components/CallEnded/CallEnded';
import DebugOverlay, { DebugBoundary, isDebugEnabled } from './components/DebugOverlay/DebugOverlay';
import GuestLeftNotification from './components/GuestLeftNotification/GuestLeftNotification';
import CallTimeWarning from './components/CallTimeWarning/CallTimeWarning';
import Controls from './components/Controls/Controls';
import LocalVideoPreview from './components/LocalVideoPreview/LocalVideoPreview';
import MenuBar from './components/MenuBar/MenuBar';
import ReconnectingNotification from './components/ReconnectingNotification/ReconnectingNotification';
import Room from './components/Room/Room';
import TapToStart from './components/TapToStart/TapToStart';

import useHasBeenConnected from './hooks/useHasBeenConnected/useHasBeenConnected';
import useCanRejoin from './hooks/useCanRejoin/useCanRejoin';
import useHeight from './hooks/useHeight/useHeight';
import useRoomState from './hooks/useRoomState/useRoomState';

const Container = styled('div')({
  display: 'grid',
  gridTemplateRows: 'auto 1fr',
});

const Main = styled('main')({
  overflow: 'hidden',
});

export default function App() {
  const roomState = useRoomState();
  const hasBeenConnected = useHasBeenConnected();
  const canRejoin = useCanRejoin();

  // Here we would like the height of the main container to be the height of the viewport.
  // On some mobile browsers, 'height: 100vh' sets the height equal to that of the screen,
  // not the viewport. This looks bad when the mobile browsers location bar is open.
  // We will dynamically set the height with 'window.innerHeight', which means that this
  // will look good on mobile browsers even after the location bar opens or closes.
  const height = useHeight();

  const callEnded = roomState === 'disconnected' && hasBeenConnected;

  // Determine which view to show:
  // - If connected/reconnecting: show the Room
  // - If disconnected and was previously connected: show CallEnded
  // - If disconnected and never connected: show LocalVideoPreview (joining state)
  const renderMain = () => {
    if (roomState !== 'disconnected') {
      return <Room />;
    }
    if (callEnded) {
      return <CallEnded canRejoin={canRejoin} />;
    }
    return <LocalVideoPreview />;
  };

  return (
    <Container style={{ height }}>
      <MenuBar callEnded={callEnded} />
      <Main>
        {renderMain()}
        {!callEnded && <Controls />}
      </Main>
      <ReconnectingNotification />
      <CallTimeWarning />
      <GuestLeftNotification />
      {roomState === 'connected' && <TapToStart />}
      {isDebugEnabled() && (
        <DebugBoundary>
          <DebugOverlay />
        </DebugBoundary>
      )}
    </Container>
  );
}
